import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Save, RotateCcw, Settings2, Wallet, Shield, DollarSign,
  Utensils, DoorOpen, Package, Users, ChevronRight, Info,
} from "lucide-react";

const GROUP_ICONS = {
  finance:   DollarSign,
  approvals: Shield,
  cash:      Wallet,
  kds:       Utensils,
  entrance:  DoorOpen,
  inventory: Package,
  payroll:   Users,
};

export default function SettingsPage() {
  const { branchId } = useAuth();
  const [schema, setSchema] = useState(null);
  const [orgRaw, setOrgRaw] = useState({});     // stored values at org scope
  const [branchRaw, setBranchRaw] = useState({}); // stored values at branch scope
  const [effective, setEffective] = useState({}); // resolved effective values
  const [activeGroup, setActiveGroup] = useState("finance");
  const [scope, setScope] = useState("organization"); // 'organization' | 'branch'
  const [drafts, setDrafts] = useState({});     // key → new value (unsaved)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, o, b] = await Promise.all([
        api.get("/settings/schema"),
        api.get("/settings/effective"),
        api.get("/settings/organization"),
        branchId ? api.get(`/settings/branch/${branchId}`) : Promise.resolve({ data: { values: {} } }),
      ]);
      setSchema(s.data);
      setEffective(e.data.values);
      setOrgRaw(o.data.values || {});
      setBranchRaw(b.data.values || {});
      setDrafts({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branchId]);

  const scopedRaw = scope === "branch" ? branchRaw : orgRaw;

  const grouped = useMemo(() => {
    if (!schema) return {};
    const out = {};
    for (const s of schema.settings) {
      (out[s.group] = out[s.group] || []).push(s);
    }
    return out;
  }, [schema]);

  const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));
  const dirty = Object.keys(drafts).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const endpoint = scope === "branch" ? `/settings/branch/${branchId}` : "/settings/organization";
      const { data } = await api.patch(endpoint, { values: drafts });
      if (scope === "branch") setBranchRaw(data.values);
      else setOrgRaw(data.values);
      toast.success(`${Object.keys(drafts).length} setting(s) saved to ${scope}`);
      setDrafts({});
      // Refresh effective
      const e = await api.get("/settings/effective");
      setEffective(e.data.values);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const resetKey = async (key) => {
    if (!scopedRaw[key] && drafts[key] === undefined) return;
    try {
      const endpoint = scope === "branch"
        ? `/settings/branch/${branchId}/${key}`
        : `/settings/organization/${key}`;
      await api.delete(endpoint);
      toast.success(`Reset to inherit`);
      setDrafts((d) => { const c = { ...d }; delete c[key]; return c; });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !schema) {
    return <div className="p-8 ink-muted text-xs tracking-[0.22em] uppercase" data-testid="settings-loading">Loading settings…</div>;
  }

  const groups = schema.groups;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="section-eyebrow flex items-center gap-2">
            <Settings2 size={12}/> Settings & Configuration · 09
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight ink">Configure the operating rules</h1>
          <p className="mt-2 ink-muted text-sm max-w-2xl">
            Every formula, threshold and policy the app enforces — tunable per organization or per branch.
            Branch overrides always win over organization defaults.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && (
            <button onClick={() => setDrafts({})}
                    data-testid="settings-discard"
                    className="btn-pill btn-pill-outline h-10">
              Discard {Object.keys(drafts).length}
            </button>
          )}
          <button onClick={save} disabled={!dirty || saving}
                  data-testid="settings-save"
                  className="btn-pill btn-pill-solid h-10 disabled:opacity-40">
            <Save size={13}/> {saving ? "Saving…" : `Save (${Object.keys(drafts).length})`}
          </button>
        </div>
      </div>

      {/* Scope switch */}
      <div className="mb-6 rounded-md hairline border p-2 flex items-center gap-2 max-w-md" data-testid="settings-scope">
        <button onClick={() => setScope("organization")}
                data-testid="scope-org"
                className={`flex-1 px-4 py-2 rounded text-[10px] uppercase tracking-[0.22em] font-bold transition ${scope === "organization" ? "bg-foreground text-background" : "ink-muted hover:bg-foreground/5"}`}>
          Organization
        </button>
        <button onClick={() => setScope("branch")}
                data-testid="scope-branch"
                disabled={!branchId}
                className={`flex-1 px-4 py-2 rounded text-[10px] uppercase tracking-[0.22em] font-bold transition ${scope === "branch" ? "bg-foreground text-background" : "ink-muted hover:bg-foreground/5"} disabled:opacity-40`}>
          This Branch {!branchId && "(select a branch)"}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* Sidebar tabs */}
        <aside className="col-span-3">
          <div className="section-eyebrow mb-3">Categories</div>
          <nav className="flex flex-col gap-1">
            {groups.map((g) => {
              const Icon = GROUP_ICONS[g.key] || Settings2;
              const active = activeGroup === g.key;
              const items = grouped[g.key] || [];
              const dirtyInGroup = items.filter((s) => drafts[s.key] !== undefined).length;
              return (
                <button key={g.key} onClick={() => setActiveGroup(g.key)}
                        data-testid={`settings-tab-${g.key}`}
                        className={`text-left p-3 rounded-md border transition group ${active ? "bg-foreground/[0.05] hairline border-foreground/20" : "border-transparent hover:bg-foreground/[0.03]"}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={14} className={active ? "ink" : "ink-subtle"}/>
                    <div className="flex-1">
                      <div className={`text-xs uppercase tracking-[0.22em] font-bold ${active ? "ink" : "ink-muted"}`}>{g.label}</div>
                      <div className="mt-0.5 text-[10px] ink-subtle leading-tight">{g.help}</div>
                    </div>
                    {dirtyInGroup > 0 && (
                      <span className="ml-1 bg-amber-500/20 text-amber-600 text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {dirtyInGroup}
                      </span>
                    )}
                    <ChevronRight size={12} className={active ? "ink" : "ink-subtle opacity-0 group-hover:opacity-100"}/>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right panel */}
        <section className="col-span-9">
          <div className="mb-6">
            <div className="text-2xl font-bold ink">{groups.find((g) => g.key === activeGroup)?.label}</div>
            <div className="ink-muted text-xs mt-1">{groups.find((g) => g.key === activeGroup)?.help}</div>
          </div>

          <div className="space-y-3">
            {(grouped[activeGroup] || []).map((spec) => (
              <SettingRow key={spec.key}
                          spec={spec}
                          effective={effective[spec.key]}
                          rawValue={scopedRaw[spec.key]}
                          draftValue={drafts[spec.key]}
                          onChange={(v) => setDraft(spec.key, v)}
                          onReset={() => resetKey(spec.key)}
                          isOverride={scopedRaw[spec.key] !== undefined}
                          scope={scope}/>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function SettingRow({ spec, effective, rawValue, draftValue, onChange, onReset, isOverride, scope }) {
  const dirty = draftValue !== undefined;
  const current = dirty ? draftValue
                : (rawValue !== undefined ? rawValue : effective);
  const isDefault = !isOverride && !dirty;

  return (
    <div className={`p-4 rounded-md border transition ${dirty ? "hairline border-amber-500/40 bg-amber-500/[0.03]" : "hairline"}`}
         data-testid={`setting-row-${spec.key}`}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-bold ink">{spec.label}</div>
            {isOverride && !dirty && (
              <span className="text-[8px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 border border-indigo-500/30" data-testid={`setting-override-${spec.key}`}>
                Overridden
              </span>
            )}
            {dirty && (
              <span className="text-[8px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30" data-testid={`setting-dirty-${spec.key}`}>
                Unsaved
              </span>
            )}
            {isDefault && (
              <span className="text-[8px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-full bg-foreground/5 ink-subtle border hairline">
                Default
              </span>
            )}
          </div>
          {spec.help && (
            <div className="mt-1 flex items-start gap-1.5 text-[11px] ink-subtle">
              <Info size={11} className="mt-0.5 flex-shrink-0"/>
              <span>{spec.help}</span>
            </div>
          )}
          <div className="mt-1 text-[10px] mono ink-subtle uppercase tracking-[0.18em]">{spec.key}</div>
        </div>

        <div className="w-72 flex items-center gap-2 flex-shrink-0">
          <ValueInput spec={spec} value={current} onChange={onChange}/>
          {isOverride && (
            <button onClick={onReset}
                    data-testid={`setting-reset-${spec.key}`}
                    title={`Remove ${scope} override, inherit from ${scope === "branch" ? "organization" : "system default"}`}
                    className="p-1.5 rounded hover:bg-foreground/10 ink-subtle hover:ink transition">
              <RotateCcw size={12}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ValueInput({ spec, value, onChange }) {
  if (spec.type === "bool") {
    return (
      <label className="flex items-center gap-2 cursor-pointer w-full">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mono" data-testid={`input-${spec.key}`}/>
        <span className="text-xs ink-muted">{value ? "Enabled" : "Disabled"}</span>
      </label>
    );
  }
  if (spec.type === "enum") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={`input-${spec.key}`}
              className="i w-full text-xs">
        {(spec.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (spec.type === "percent") {
    return (
      <div className="flex items-center gap-1 w-full">
        <input type="number" step="0.01" min={0} max={100}
               value={value !== undefined ? (Number(value) * 100).toFixed(2) : 0}
               onChange={(e) => onChange(parseFloat(e.target.value) / 100)}
               data-testid={`input-${spec.key}`}
               className="i mono w-full text-right"/>
        <span className="ink-subtle text-xs mono">%</span>
      </div>
    );
  }
  if (spec.type === "money") {
    return (
      <div className="flex items-center gap-1 w-full">
        <span className="ink-subtle text-xs mono">₱</span>
        <input type="number" step="0.01" min={0}
               value={value ?? 0}
               onChange={(e) => onChange(parseFloat(e.target.value))}
               data-testid={`input-${spec.key}`}
               className="i mono w-full text-right"/>
      </div>
    );
  }
  if (spec.type === "seconds") {
    return (
      <div className="flex items-center gap-1 w-full">
        <input type="number" step="1" min={0}
               value={value ?? 0}
               onChange={(e) => onChange(parseInt(e.target.value || 0))}
               data-testid={`input-${spec.key}`}
               className="i mono w-full text-right"/>
        <span className="ink-subtle text-xs mono">s</span>
      </div>
    );
  }
  if (spec.type === "int") {
    return (
      <input type="number" step="1"
             min={spec.min} max={spec.max}
             value={value ?? 0}
             onChange={(e) => onChange(parseInt(e.target.value || 0))}
             data-testid={`input-${spec.key}`}
             className="i mono w-full text-right"/>
    );
  }
  if (spec.type === "number") {
    return (
      <input type="number" step="0.01"
             min={spec.min} max={spec.max}
             value={value ?? 0}
             onChange={(e) => onChange(parseFloat(e.target.value))}
             data-testid={`input-${spec.key}`}
             className="i mono w-full text-right"/>
    );
  }
  return (
    <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
           data-testid={`input-${spec.key}`}
           className="i w-full"/>
  );
}
